import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import { CurrentUserContext } from "../../../context/CurrentUserContext";
import { useParams, useNavigate, Link } from "react-router-dom";
import { slugify } from "../../../utils/slugify";
import "./LecturesHome.css";
import { useLanguage } from "../../../context/LanguageContext";
import { useLevelContentData } from "../../../utils/hooks/translateLevelContentDataHook.js";
import { useTranslation } from "../../../utils/hooks/translateHook";
import { transformLevelContent } from "../../../utils/transformLevelContent";
import LectureProgressItem from "../LectureProgressItem";
import { removeLessonNumber } from "../../../utils/titles.js";
import useLectureProgress from "../../../utils/hooks/useLectureProgress.js";
import DownArrow from "../../../components/ContentHtml/styles/icon/DownArrow.svg"; 
import { youtubeLessons } from "../../../utils/youtubeLessonsMap.js";
import { courseRegistry } from "../../../courses/courseRegistry";
import { saveLessonProgress  } from "../../../utils/API/progressApi.js";

export const API_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:4000/api"
    : process.env.REACT_APP_API_URL;

    
const LecturesHome = ({ user, subject }) => {
  const { currentUser, setCurrentUser } = useContext(CurrentUserContext);
  const [selectedLevelLectures, setSelectedLevelLectures] = useState([]);
  const [openSubjectIndex, setOpenSubjectIndex] = useState(null);
  const [note, setNote] = useState("");
  const [userClickedSubject, setUserClickedSubject] = useState(false);
  const { courseSlug, lectureSlug } = useParams();
  const [courseData, setCourseData] = useState(null);
 

  const navigate = useNavigate();
  const { language } = useLanguage();

   //Creates mini ID's global so 1.1 or 1.1.2  or 2.4 2.4.3 etc finds the translation e
  const extractPrefix = (str = "") => {  //used by UI - lecture link hls fix as well besides the level check for the users
    const match = String(str).match(/(\d+(?:[\.-]\d+)+)/);
    return match ? match[1].replace(/-/g, ".") : null;
  };
  const getLectureText = (lecture) => {
    if (typeof lecture === "string") return lecture;
    if (typeof lecture?.title === "string") return lecture.title;
    if (typeof lecture?.title?.title === "string") return lecture.title.title;
    return "";
  };

  const getLecturePrefix = (lecture) => {
    const text = getLectureText(lecture);
    const match = String(text).match(/(\d+)[\.-](\d+)[\.-](\d+)/);
    return match ? `${match[1]}.${match[2]}.${match[3]}` : null;
  };

/*   const translatedLevelContentData = useLevelContentData() || [];
  const transformedData = transformLevelContent(translatedLevelContentData, language); */

  const courseConfig = courseRegistry[courseSlug];
  const rawLevelContentData = courseConfig?.levelContentData || [];

  const englishKeyMap = useMemo(() => {
    const map = {};

    rawLevelContentData.forEach(level => {
      level.subjects?.forEach(subject => {
        subject.lectures?.forEach(lecture => {
          const key = typeof lecture === "string" ? lecture : lecture.key;
          const prefixMatch = key.match(/(\d+)[\.-](\d+)[\.-](\d+)/);

          if (prefixMatch) {
            const prefix = `${prefixMatch[1]}.${prefixMatch[2]}.${prefixMatch[3]}`;
            map[prefix] = key; // store original English key
          }
        });
      });
    });

    return map;
  }, [rawLevelContentData]);

  const translatedLevelContentData = useLevelContentData(rawLevelContentData, courseSlug) || [];
  const transformedData = transformLevelContent(translatedLevelContentData, language)



  const t = useTranslation();
  const accessibleLevels = transformedData
    .filter((item) => user.subscriptionLevel >= item.level && item.level !== 999)
    .sort((a, b) => a.level - b.level);

  const levelInfo = currentUser?.subscriptionLevel
    ? transformedData.find((item) => item.level === currentUser.subscriptionLevel)
    : null;
  const levelTitle = levelInfo?.title || "Unknown";
 
 const getLectureSlug = (lecture) => {
  const raw = typeof lecture === "string" ? lecture : lecture?.title;
  
  const str = typeof raw === "string" ? raw : (typeof raw?.title === "string" ? raw.title : "");
    if (!str) return lectureSlug || "lecturesTree";

  const match = str.match(/(\d+)[\.-](\d+)[\.-](\d+)/);
  if (!match) return lectureSlug || "lecturesTree";

  return `${match[1]}-${match[2]}-${match[3]}`;
};
      
  const findLectureBySlug = () => {
    if (!lectureSlug) return null;

    const slugPrefix = lectureSlug.split("-").slice(0, 3).join(".");
    for (const level of transformedData) {
      for (const subject of level.subjects || []) {
        for (const lecture of subject.lectures || []) {
          const prefix = getLecturePrefix(lecture);

          if (prefix === slugPrefix) {
            return typeof lecture === "string"
              ? { title: lecture, stableKey: lecture }
              : { ...lecture, title: getLectureText(lecture) };
          }
        }
      }
    }
    return null;
  };

  //we are trying to get courseData as we consoled below with this piece
  const matchedLecture = findLectureBySlug();

  let lectureParagraphs = [];

  if(matchedLecture && courseData) {
    const prefixMatch = matchedLecture.title.match(/(\d+)\.(\d+)\.(\d+)/);

    if (prefixMatch) {
      const numericKey = prefixMatch.slice(1).join("");
      const baseKey = `course.${courseSlug}.${numericKey}`;

      lectureParagraphs = Object.keys(courseData)
        .filter(key => key.startsWith(`${baseKey}.p`))
        .sort((a, b) => {
          const numA = parseInt(a.split(".p")[1]);
          const numB = parseInt(b.split(".p")[1]);
          return numA - numB;
        })
        .map(key => courseData[key]);
    }
  }


  const currentSubject = useMemo(() => { //for mobile Navigation bar
    if (!matchedLecture) return null;

    for (const level of accessibleLevels) {
      for (const subject of level.subjects || []) {
        if (subject.lectures?.some(l => {
          const prefix = getLecturePrefix(l);
          const slugPrefix = lectureSlug.split("-").slice(0, 3).join(".");
          return prefix === slugPrefix
        })) {
          return subject;
        }
      }
    }
    return null;
  }, [matchedLecture, lectureSlug, accessibleLevels]);


  useEffect(() => {
    const userLevel = user.subscriptionLevel;
    const levelData = transformedData.find((l) => l.level === userLevel);
    if (levelData) {
      const lectureGroups =
        levelData.subjects?.map((subject) => ({
          title: subject.title,
          list: subject.lectures || [],
        })) || [];

      setSelectedLevelLectures((prev) => {
        const prevString = JSON.stringify(prev);
        const nextString = JSON.stringify(lectureGroups);
        return prevString !== nextString ? lectureGroups : prev;
      });
    }
  }, [user.subscriptionLevel, transformedData]);

  //Content by membersip level check here***
  useEffect(() => {
    if (!lectureSlug || !user?.subscriptionLevel || !transformedData.length) {
      return;
    }
    const slugText = lectureSlug;
    const userLevel = user.subscriptionLevel;
    const extractPrefix = (str) => {
      const match = str.match(/^(\d+)[\.-](\d+)[\.-](\d+)/);
      return match ? `${match[1]}.${match[2]}.${match[3]}` : null;
    };
    const slugPrefix = lectureSlug.split("-").slice(0, 3).join(".");
    /* console.log("prefix to match:", slugPrefix); */
    let requiredLevel = null;
    const allLectures = transformedData.flatMap((level) =>
      (level.subjects || []).flatMap((subject) =>
        (subject.lectures || []).map((lecture) => ({
          level: level.level,
          title: getLectureText(lecture),
        }))
      )
    );
   
    const matched = allLectures.find((lecture) => {
      const lecturePrefix = getLecturePrefix(lecture.title);
      return lecturePrefix && lecturePrefix === slugPrefix;
    });

    if (matched) {
      requiredLevel = matched.level;
      console.log(
        `Match found ➤ Title: ${matched.title} | Level: ${requiredLevel}`
      );
    }

    if (requiredLevel && userLevel < requiredLevel) {
      console.warn("Access denied: video beyond user level");
      navigate("/upgrade");
    }
  }, [lectureSlug, user?.subscriptionLevel, transformedData]);

  //to get translationsCourseData the deepest level in the content
  useEffect(() => {
    if (!courseSlug) return;

    import(`../../../utils/translations/${courseSlug}/${language}-translationsCourseData.js`)
      .then(module => {
        console.log("Loaded translationsCourseData module:", module);
        console.log("Loaded language block:", module.courseData?.[language]);

        setCourseData(module.courseData?.[language] || {});
      })
      .catch(() => {
        import(`../../../utils/translations/${courseSlug}/en-translationsCourseData.js`)
          .then(fallback => {
            console.log("Loaded fallback translationsCourseData:", fallback);
            console.log("Loaded fallback language block:", fallback.course?.en);

            setCourseData(fallback.courseData?.en || {});
          });
      });

  }, [courseSlug, language]);


useEffect(() => {
  if (!matchedLecture || userClickedSubject) return;

  const subjects = accessibleLevels.flatMap((level) => level.subjects || []);
  const subjectIndex = subjects.findIndex((subject) =>
    subject.lectures.some((lecture) => {
      const title = typeof lecture === "string" ? lecture : lecture.title;
      const currentPrefix = lectureSlug.split("-").slice(0,3).join('.');
      return extractPrefix(title) === currentPrefix//slugify(lecture.key) === lectureSlug;
    })
  );

  if (subjectIndex !== -1) {
    setOpenSubjectIndex(subjectIndex);
  }
}, [matchedLecture, lectureSlug, accessibleLevels, userClickedSubject]);

useEffect(() => {
  setUserClickedSubject(false);
}, [lectureSlug]);

useEffect(() => {
  window.scrollTo({ top: 0, behavior: "smooth" });
}, [lectureSlug]);

  console.log("Matched lecture:", matchedLecture);
  const { completed, toggleComplete } = useLectureProgress(lectureSlug, true); //to find the current lecture

  const orderedLectures = accessibleLevels
  .flatMap(level => level.subjects || [])
  .flatMap(subject => subject.lectures || [])
  .map(lecture => { const title = typeof lecture === "string" ? lecture : lecture.title;
    return { title, slug: getLectureSlug(lecture),};
  });

  const currentIndex = orderedLectures.findIndex( lec => lec.slug === lectureSlug);
  const nextLecture = orderedLectures[currentIndex + 1];

  const goToNextLecture = () => {
    if (!nextLecture) return;
    
    navigate(`/course/${courseSlug}/lectures/${nextLecture.slug}`);
    setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
          }, 500); 
};
 

console.log("ALL youtubeLessons:", youtubeLessons);
console.log("Current lectureSlug:", lectureSlug);
console.log("Direct lookup:", youtubeLessons[matchedLecture?.slug]);

  //for youtube to capture correct lessons for presenting embedded
/*   const lectureId = matchedLecture?.key
    ?.toLowerCase()
    .replace(/\./g, "-")
    .replace(/\s+/g, "-"); */
  const youtubeKey = useMemo(() => {
    if (!lectureSlug) return null;
    return Object.keys(youtubeLessons).find((k) => k.startsWith(`${lectureSlug}-`)) || null;
  }, [lectureSlug]);  

  useEffect(() => {
    if (!courseSlug || !lectureSlug || !matchedLecture?.title) return;

    const startedAt = Date.now();

    const saveProgress = () => {
      const secondsSpent = Math.floor((Date.now() - startedAt) / 1000);

      saveLessonProgress({
        courseSlug,
        lessonSlug: lectureSlug,
        lessonType: "text",
        title: matchedLecture.title,
        secondsSpent
      })
      .then((data) => {
        console.log("Text Lesson Progress Saved:", data);
      })
      .catch((err) => {
        console.log("Text lesson progress error:", err);
      });
    };

    saveProgress();

    const interval = setInterval(saveProgress, 30000);
    return () => {
      clearInterval(interval);
      saveProgress();
    };

  }, [courseSlug, lectureSlug, matchedLecture?.title]);

  const handleToggleComplete = async () => {
    toggleComplete();

    if (!completed && courseSlug && lectureSlug && matchedLecture?.title) {
      await saveLessonProgress({ 
        courseSlug, lessonSlug: lectureSlug, lessonType: "text", title: matchedLecture.title, markCompleted: true 
      });
    }
  };
  

  return (
    <div className="lectures-page">
      <div className="lectures-layout">
        <div className="lectures-sidebar">
          <h4 className="title-sidebar"> {t("lessons")} </h4>
            {accessibleLevels
              .flatMap((level) => level.subjects || [])
              .map((subject, idx) => (
                <div key={idx} className={`subject-acc ${ openSubjectIndex === idx ? "open" : "" }`}>
                  <div
                    className="lectures-title"
                    onClick={() => {setUserClickedSubject(true); 
                                    setOpenSubjectIndex(openSubjectIndex === idx ? null : idx);}}>
                    {subject.title.toUpperCase()}
                    {openSubjectIndex === idx && (<img src={DownArrow} alt="open" className="arrow-icon" />)}
                  </div>
                  {Array.isArray(subject.lectures) &&
                    subject.lectures.length > 0 &&
                    openSubjectIndex === idx && (
                      <ul className="lectures-list">
                        {subject.lectures.map((lecture, i) => {
                            const isString = typeof lecture === "string";
                            const stableKey = isString ? lecture : lecture.stableKey;

                            const title = isString 
                              ? lecture 
                              : (typeof lecture.title === "string"
                                  ? lecture.title
                                  : (typeof lecture.title?.title === "string" ? lecture.title.title : ""));

                            const currentPrefix = lectureSlug  ? lectureSlug.split("-").slice(0, 3).join(".")  : null;
                          return (
                              <LectureProgressItem
                                key={stableKey || i}
                                title={removeLessonNumber(title) || ""}   
                                realTitle={stableKey}  
                                to={`/course/${courseSlug}/lectures/${getLectureSlug(lecture)}`}
         
                                className={
                                  extractPrefix(stableKey) === currentPrefix //lectureSlug.split("-").slice(0, 3).join(".")
                                  ? "active-lecture"
                                  : ""
                                }
                                allowToggle={true}
                              />
                          );
                        })}
                      </ul>
                    )}
                </div>
            ))}
        </div>
        {!lectureSlug ? (
          <div className="lectures-landing-wrapper">
            <div className="lectures-landing-bg">
              <div className="landingImage-containerbubble">
                <div className="landingImage-overlay">
                  <h1 className="landing-title"> {t('lectursBubble')} </h1>
                  <p className="landing-subtitle"> {t('lectursBubble2')} </p>
                </div>
              </div>
            </div>
          </div>
          ) : matchedLecture ? (
            <div className="frame">
              {currentSubject && (
                <div className="frame-header"> 
                  <span className="frame-chapter"> {currentSubject.title.toUpperCase()}</span>
                </div>
              )}
              <div className={`frame__inner ${!lectureSlug ? "landing" : ""}`}>
                <div className="lecture-box">
                  <div className="lecture-item-les">
                  
                    <div className="mobile-bar"> {/* ONLY FOR MOBILE */}
                      <span className="mobile link" onClick={() => navigate(`/course/${courseSlug}/lectures/lecturesTree`)}> {t("lectures")} </span>
                      <span className="divider">|</span>
                      {currentSubject && (
                        <>
                          <span className="mobile link" onClick={() => navigate(`/course/${courseSlug}/lecturesTree?subject=${slugify(currentSubject.title)}`)}>
                            {currentSubject.title.toUpperCase()}
                          </span> 
                        </>
                      )}
                      {/* <span className="mobile active"> {matchedLecture.title.toUpperCase()}</span> */}
                    </div>

                    <h3>{matchedLecture.title}</h3>

                    {Array.isArray(lectureParagraphs) && lectureParagraphs.length > 0 ? (
                      lectureParagraphs.map((p, idx) => {
                        const processed = p.replace(/(&lt;[^&]*&gt;)/g, "<code>$1</code>");
                        return <div key={idx} dangerouslySetInnerHTML={{ __html: processed }} />;
                      })
                    ) : (
                      <p>[No content found]</p>
                    )}

                    {youtubeKey && youtubeLessons[youtubeKey] && (
                      <div className="lecture-youtube">
                        <iframe
                          title="Recommended lesson video"
                          width="100%"
                          height="400"
                          src={`https://www.youtube.com/embed/${youtubeLessons[youtubeKey]}?rel=0`}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                            referrerPolicy="strict-origin-when-cross-origin"
                        />
                      </div>
                    )}

                    <div className="lecture-actions">
                      {nextLecture && (
                        <button onClick={goToNextLecture} 
                                className={`next-lecture-btn ${!completed ? "disabled" : ""}`}
                                disabled={!completed}>
                                {t("nextlesson")}
                        </button>
                      )}
                      <button onClick={handleToggleComplete} className={`finish-btn ${completed ? "completed" : ""}`}>
                        {completed ? t("chapterCompleted") : t("markAsFinished")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            ) : (<p style={{ textAlign: "center", color: "#888" }}> Lecture not found or not accessible.</p>)
        }
      </div>          
    </div>
  );
};

export default LecturesHome;
